/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { useTranslation } from 'react-i18next';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';
import { geoCentroid, geoContains } from 'd3-geo';
import { Country } from '../types';
import { destinationLabel } from '../countryLabel';

// Topojson hébergé localement dans /public/data — pas de dépendance réseau au runtime.
const GEO_URL = '/data/world-110m.json';

const MAP_WIDTH = 800;
const MAP_HEIGHT = 400;
const PROJECTION_SCALE = 110;

/** Vue par défaut : le planisphère entier. */
const DEFAULT_CENTER: [number, number] = [0, 0];
const DEFAULT_ZOOM = 1;
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
/** Niveau appliqué au recentrage automatique sur le pays choisi. */
const FOCUS_ZOOM = 3;

const STROKE = '#8CCDCD';
const FILL_AVAILABLE = '#f4f7f8';
const FILL_UNAVAILABLE = '#fafbfb';
const HIGHLIGHT_FILL = '#cde6e6';
const SELECTED_FILL = '#a8dada';
const PING_COLOR = '#2f6975';

interface CountryPingMapProps {
  /** Pays sélectionné actuellement (pour afficher le ping). */
  selected: Country | null;
  /** Liste des pays disponibles côté back (seuls cliquables). */
  countries: Country[];
  /** Callback déclenché quand l'utilisateur clique sur un pays disponible. */
  onCountrySelect?: (country: Country) => void;
  /** Hauteur du conteneur. Par défaut 280px. */
  height?: number;
}

/** Distance angulaire euclidienne entre 2 points (degrés). */
const dist = (a: [number, number], b: [number, number]): number => {
  const dLng = a[0] - b[0];
  const dLat = a[1] - b[1];
  return Math.sqrt(dLng * dLng + dLat * dLat);
};

const CountryPingMap: React.FC<CountryPingMapProps> = ({
  selected,
  countries,
  onCountrySelect,
  height = 280,
}) => {
  const { t, i18n } = useTranslation('sante-mobilite');

  // Zoom et centre pilotés ici plutôt que laissés à ZoomableGroup seul : on veut
  // pouvoir recentrer sur le pays choisi, et offrir des boutons — la molette et
  // le pincement ne sont pas découvrables, et sont inutilisables au clavier.
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [center, setCenter] = useState<[number, number]>(DEFAULT_CENTER);

  // Recentre sur le pays choisi. Ne se déclenche que sur un CHANGEMENT de
  // sélection : sinon le moindre rendu annulerait le déplacement que
  // l'utilisateur vient de faire à la main.
  useEffect(() => {
    if (!selected) return;
    const { lat, lng } = selected;
    if (typeof lat !== 'number' || typeof lng !== 'number') return;
    setCenter([lng, lat]);
    setZoom(z => (z < FOCUS_ZOOM ? FOCUS_ZOOM : z));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

  const resetView = () => {
    setCenter(DEFAULT_CENTER);
    setZoom(DEFAULT_ZOOM);
  };

  const selectedCoords: [number, number] | null = useMemo(() => {
    if (
      selected &&
      typeof selected.lng === 'number' &&
      typeof selected.lat === 'number'
    ) {
      return [selected.lng, selected.lat];
    }
    return null;
  }, [selected]);

  // Pays back avec coords valides (seuls candidats au matching).
  const placeable = useMemo(
    () =>
      countries.filter(
        c => typeof c.lat === 'number' && typeof c.lng === 'number',
      ),
    [countries],
  );

  /**
   * Pour une feature topojson, retourne TOUS les pays back dont les coords
   * tombent à l'intérieur de la géométrie. Sert à colorer la feature comme
   * "disponible" dès qu'au moins un pays back y est rattaché (USA contigus,
   * Alaska, Hawaï peuvent tous tomber dans la feature "United States of
   * America" du topojson).
   */
  const countriesInFeature = (
    geometry: Parameters<typeof geoContains>[0],
  ): Country[] =>
    placeable.filter(c =>
      geoContains(geometry, [c.lng as number, c.lat as number]),
    );

  /**
   * Liste des pays satellites/micro-États qui partagent une feature topojson
   * world-110m avec un voisin plus gros. On les exclut explicitement de la
   * sélection-par-clic-map pour que le pays principal gagne toujours.
   *
   * Ces pays restent **sélectionnables via l'autocomplete dropdown**, qui n'a
   * pas cette ambiguïté géographique — seul le clic sur la map est ambigu.
   *
   * Pattern matching insensible à la casse + tolérant aux variantes (accents,
   * articles, traductions FR/EN), car le `name` côté back est en français
   * mais on n'a aucune garantie sur la casse exacte.
   */
  const MAP_SATELLITES = new Set(
    [
      // Micro-États enclavés dans France métropolitaine
      'andorre',
      'andorra',
      'monaco',
      // Micro-États enclavés dans Italie
      'saint-marin',
      'san marino',
      'vatican',
      'cité du vatican',
      'vatican city',
      // Micro-États enclavés en Suisse/Autriche
      'liechtenstein',
      // États-Unis non-contigus (Alaska, Hawaï parfois rattachés au polygone USA)
      // — ils ont en général un id ISO distinct côté back donc rarement listés
      // ici, mais on les couvre par sécurité.
    ].map(s => s.toLowerCase()),
  );

  const isSatellite = (c: Country): boolean =>
    MAP_SATELLITES.has(c.name.trim().toLowerCase());

  const handleCountryClick = (
    geo: Parameters<typeof geoContains>[0],
    candidates: Country[],
  ) => {
    if (!onCountrySelect || candidates.length === 0) return;

    // 1 seul candidat : pas de question, on sélectionne.
    if (candidates.length === 1) {
      onCountrySelect(candidates[0]);
      return;
    }

    // Plusieurs candidats : on retire d'abord les pays satellites connus
    // (Andorre, Monaco, Vatican, etc.) qui partagent leur polygone avec un
    // voisin plus gros mais qu'on ne veut JAMAIS sélectionner via un clic map.
    const nonSatellites = candidates.filter(c => !isSatellite(c));

    // S'il reste exactement un pays non-satellite, c'est gagné — c'est le pays
    // principal de la feature.
    if (nonSatellites.length === 1) {
      onCountrySelect(nonSatellites[0]);
      return;
    }

    // Cas dégradé : soit tous les candidats sont des satellites (improbable —
    // arriverait si le back retournait Andorre + Monaco dans une même feature
    // sans le pays principal), soit on a plusieurs non-satellites (eg. USA +
    // Canada dans une feature étrange). On retombe sur le centroid : on prend
    // le pays dont les coords sont les plus proches du centre géographique
    // de la feature, parmi les non-satellites si possible sinon tous.
    const pool = nonSatellites.length > 0 ? nonSatellites : candidates;
    const centroid = geoCentroid(geo) as [number, number];
    let best: Country = pool[0];
    let bestDist = Infinity;
    for (const c of pool) {
      const d = dist(centroid, [c.lng as number, c.lat as number]);
      if (d < bestDist) {
        bestDist = d;
        best = c;
      }
    }
    onCountrySelect(best);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height,
        backgroundColor: '#fafcfc',
        borderRadius: 3,
        border: '1px solid #e0eaea',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: PROJECTION_SCALE }}
        width={MAP_WIDTH}
        height={MAP_HEIGHT}
        style={{ width: '100%', height: '100%' }}
      >
        <ZoomableGroup
          center={center}
          zoom={zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          // On récupère la vue après un déplacement à la souris pour que les
          // boutons +/- repartent d'où l'utilisateur s'est arrêté, au lieu de
          // le téléporter au dernier état connu du composant.
          onMoveEnd={({ coordinates, zoom: z }) => {
            setCenter(coordinates as [number, number]);
            setZoom(z);
          }}
        >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map(geo => {
              const candidates = countriesInFeature(geo);
              const isAvailable = candidates.length > 0;
              const isSelected =
                isAvailable &&
                !!selected &&
                candidates.some(c => c.id === selected.id);

              const baseFill = isSelected
                ? SELECTED_FILL
                : isAvailable
                ? FILL_AVAILABLE
                : FILL_UNAVAILABLE;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={baseFill}
                  stroke={STROKE}
                  strokeWidth={isAvailable ? 0.4 : 0.2}
                  onClick={
                    isAvailable
                      ? () => handleCountryClick(geo, candidates)
                      : undefined
                  }
                  style={{
                    default: {
                      outline: 'none',
                      cursor: isAvailable ? 'pointer' : 'default',
                    },
                    hover: {
                      outline: 'none',
                      fill: isAvailable ? HIGHLIGHT_FILL : FILL_UNAVAILABLE,
                    },
                    pressed: { outline: 'none' },
                  }}
                />
              );
            })
          }
        </Geographies>

        {selectedCoords && (
          <Marker coordinates={selectedCoords}>
            {/* Halo pulsant */}
            <circle r={10} fill={PING_COLOR} opacity={0.4}>
              <animate
                attributeName="r"
                from="6"
                to="16"
                dur="1.6s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                from="0.5"
                to="0"
                dur="1.6s"
                repeatCount="indefinite"
              />
            </circle>
            {/* Point central */}
            <circle r={4} fill={PING_COLOR} stroke="#fff" strokeWidth={1} />
          </Marker>
        )}
        </ZoomableGroup>
      </ComposableMap>

      {/* Commandes de zoom. La molette et le pincement fonctionnent aussi, mais
          rien ne les annonce : ces boutons rendent le zoom découvrable, et
          utilisable sans souris. */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {[
          { key: 'in', icon: <AddIcon fontSize="small" />, label: t('step2.map_zoom_in'), onClick: () => setZoom(z => clampZoom(z * 1.5)), disabled: zoom >= MAX_ZOOM },
          { key: 'out', icon: <RemoveIcon fontSize="small" />, label: t('step2.map_zoom_out'), onClick: () => setZoom(z => clampZoom(z / 1.5)), disabled: zoom <= MIN_ZOOM },
          { key: 'reset', icon: <CenterFocusStrongIcon fontSize="small" />, label: t('step2.map_zoom_reset'), onClick: resetView, disabled: zoom === DEFAULT_ZOOM && center[0] === DEFAULT_CENTER[0] && center[1] === DEFAULT_CENTER[1] },
        ].map(b => (
          <Tooltip key={b.key} title={b.label} placement="left" arrow>
            <span>
              <IconButton
                size="small"
                aria-label={b.label}
                onClick={b.onClick}
                disabled={b.disabled}
                sx={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e0eaea',
                  color: '#2f6975',
                  '&:hover': { backgroundColor: '#f0f7f7' },
                  '&.Mui-disabled': { backgroundColor: '#ffffff', color: '#c3d3d3' },
                }}
              >
                {b.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </Box>

      {selected && !selectedCoords && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          {t('step2.map_country_unmapped', {
            country: destinationLabel(selected.name, i18n.language),
          })}
        </Typography>
      )}
    </Box>
  );
};

export default CountryPingMap;
